import React, {
  createContext, useContext, useState,
  useCallback, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import tokenManager from '../services/tokenManager';
import api, { refreshAccessToken } from '../services/api';

const AuthContext = createContext();


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]         = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const initializationStarted = React.useRef(false);

  useEffect(() => {
    const initializeAuth = async () => {
      if (initializationStarted.current) return;
      initializationStarted.current = true;

      try {
        await refreshAccessToken();
        const tokenUser = tokenManager.getUserFromToken();
        if (tokenUser) {
          try {
            const res = await api.get('/auth/me');
            setUser(res.data);
          } catch {
            setUser(tokenUser); // fallback to token data if /me fails
          }
        }
        // If 401, cookie doesn't exist or expired — user stays logged out
      } catch {
        // Refresh failed — clear any stale access token so subsequent API
        // calls don't send it and trigger repeated "session expired" alerts
        tokenManager.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ── Auth actions ──────────────────────────────────────────────────────────

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    try {
      const result = await authService.login(email, password);
      if (result.success) setUser(result.user);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    setIsLoading(true);
    try {
      const result = await authService.adminLogin(email, password);
      if (result.success) setUser(result.user);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendOtp = useCallback(async (phone) => {
    return authService.sendOtp(phone);
  }, []);

  const phoneLogin = useCallback(async (phone, otp) => {
    setIsLoading(true);
    try {
      const result = await authService.phoneLogin(phone, otp);
      if (result.success) setUser(result.user);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const phoneRegister = useCallback(async (name, phone, otp, email, ageDeclarationAcceptedAt) => {
    setIsLoading(true);
    try {
      const result = await authService.phoneRegister(name, phone, otp, email, ageDeclarationAcceptedAt);
      if (result.success) setUser(result.user);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const acceptAgeDeclaration = useCallback(async () => {
    const result = await authService.acceptAgeDeclaration();
    if (result.success) setUser(result.user);
    return result;
  }, []);

  const register = useCallback(async (name, email, password) => {
    setIsLoading(true);
    try {
      const result = await authService.register(name, email, password);
      if (result.success) setUser(result.user);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const result = await authService.updateProfile(updates);
    if (result.success) setUser(result.user);
    return result;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    return authService.changePassword(currentPassword, newPassword);
  }, []);

  const setPassword = useCallback(async (payload) => {
    return authService.setPassword(payload);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    return authService.requestPasswordReset(email);
  }, []);

  const refreshUser = useCallback(async () => {
    const result = await authService.getCurrentUser();
    if (result.success) setUser(result.user);
    return result;
  }, []);

  // ── Derived state — single source of truth ────────────────────────────────
  // isAuthenticated is derived from user state ONLY.
  // Previously it also checked tokenManager.hasToken() which caused a
  // brief split-brain during session restore and led to wrong redirects.
  const isAuthenticated = !!user;
  // Both admin tiers ('admin' = Normal Admin, 'super_admin' = Master Control
  // Admin) get dashboard/route access. Only Master Control Admins may manage
  // roles — gate role-management UI on isSuperAdmin.
  const isAdmin         = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin    = user?.role === 'super_admin';

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    isLoading,
    login,
    adminLogin,
    sendOtp,
    phoneLogin,
    phoneRegister, 
    register,
    logout,
    updateProfile,
    changePassword,
    setPassword,
    requestPasswordReset,
    refreshUser,
    acceptAgeDeclaration,
    getAccessToken: () => tokenManager.getAccessToken(),
    hasValidToken:  () => tokenManager.hasToken(),
  }), [
    user, isAuthenticated, isAdmin, isSuperAdmin, isLoading,
    login, adminLogin, sendOtp, phoneLogin, phoneRegister, register, logout, updateProfile,
    changePassword, setPassword, requestPasswordReset, refreshUser, acceptAgeDeclaration,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;